import { Alert, Box, Container, Paper, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { verifyMagicLink } from "../api/client";
import { PageLoadingState } from "../components/pages/PageLoadingState";
import { useAuth } from "../contexts/AuthContext";

export function MagicLinkVerify() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setError("Missing token");
      setLoading(false);
      return;
    }
    const abort = new AbortController();
    setLoading(true);
    setError(null);
    verifyMagicLink(token, abort.signal)
      .then(async (res) => {
        if (abort.signal.aborted) return;
        await auth.setTokens({ accessToken: res.access_token, refreshToken: res.refresh_token });
        navigate("/home", { replace: true });
      })
      .catch((e) => {
        if (abort.signal.aborted) return;
        setError(e instanceof Error ? e.message : "Failed to verify magic link");
      })
      .finally(() => {
        if (abort.signal.aborted) return;
        setLoading(false);
      });
    return () => abort.abort();
  }, [auth, navigate, token]);

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider" }}>
          <PageLoadingState variant="inline" message="Signing you in…" />
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Sign-in link
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Verifying your link…
          </Typography>
        </Box>

        <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider" }}>
          {error ? <Alert severity="error">{error}</Alert> : <Alert severity="success">Signed in.</Alert>}
        </Paper>
      </Stack>
    </Container>
  );
}

